import { useState } from "react";
import styled from "styled-components";
import { Color, Flex, Grid, Icon, Text } from "src/elements";
import { useTranslation } from "src/i18n";
import {
  flowUpdateSkipReasons,
  type ResponseGetMerchflows_merchflowId_FlowUpdate_storePogTriggerId_,
  type FlowUpdateStorePogIdEntry,
} from "@CommonApi/merchflows/_merchflowId_/flow-update/_storePogTriggerId_";

type RawDetail = ResponseGetMerchflows_merchflowId_FlowUpdate_storePogTriggerId_["details"][number];
export type UpdateReportDetail = Omit<RawDetail, "changeType"> & {
  changeType: RawDetail["changeType"] | "FACINGS_INCREASED" | "FACINGS_DECREASED";
};
type PlacementAttempt = NonNullable<UpdateReportDetail["placementAttempts"]>[number];
type ChangeType = UpdateReportDetail["changeType"];

interface Props {
  details: UpdateReportDetail[];
  storePogIds: Record<string, FlowUpdateStorePogIdEntry>;
  buildStorePogUrl: (storeCode: string, storePogId: number) => string;
}

interface GroupedStore {
  storeCode: string;
  issueCount: number;
  insertions: UpdateReportDetail[];
  swaps: UpdateReportDetail[];
  deletions: UpdateReportDetail[];
  expansions: UpdateReportDetail[];
  reductions: UpdateReportDetail[];
}

const TableContainer = styled(Flex)`
  width: 100%;
  border: 1px solid ${Color.lightGrayHover};
  border-radius: 3px;
  background: ${Color.white};
`;

const ChevronWrapper = styled(Flex)<{ $isOpen: boolean }>`
  transform: ${({ $isOpen }) => ($isOpen ? "rotate(90deg)" : "none")};
  transition: transform 200ms ease;
  align-items: center;
  justify-content: center;
`;

const GridHeader = styled(Grid)`
  grid-template-columns: 3fr 2.5fr 120px 4fr;
  border-bottom: 2px solid ${Color.lightGrayHover};
  background: ${Color.lightGray};
  padding: 10px 16px;
  align-items: center;
`;

const HeaderCell = styled(Text)`
  font-weight: bold;
  font-size: 11px;
  color: ${Color.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StoreHeaderRow = styled(Flex)`
  background: ${Color.greenSmoke};
  border-bottom: 1px solid ${Color.lightGrayHover};
  padding: 12px 16px;
  align-items: center;
  cursor: pointer;
  user-select: none;
  gap: 12px;

  &:hover {
    background: ${Color.greenSmokeHover};
  }
`;

const IssueBadge = styled(Flex)`
  background: ${Color.red};
  color: ${Color.white};
  font-size: 11px;
  font-weight: bold;
  padding: 2px 8px;
  border-radius: 12px;
  align-items: center;
  gap: 4px;
`;

const PogLabel = styled(Text)`
  font-size: 11px;
  color: ${Color.textSecondary};
`;

const PogLink = styled.a`
  font-size: 11px;
  color: ${Color.primaryActive};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const OperationHeaderRow = styled(Flex)`
  background: ${Color.opacitySky};
  border-bottom: 1px solid ${Color.lightGrayHover};
  padding: 10px 24px;
  align-items: center;
  cursor: pointer;
  user-select: none;
  gap: 10px;

  &:hover {
    background: ${Color.lightGrayHover};
  }
`;

const ProductRowGrid = styled(Grid)<{ $isClickable?: boolean }>`
  grid-template-columns: 3fr 2.5fr 120px 4fr;
  border-bottom: 1px solid ${Color.lightGrayHover};
  padding: 12px 16px;
  align-items: center;
  background: ${Color.white};
  cursor: ${({ $isClickable }) => ($isClickable ? "pointer" : "default")};
  user-select: ${({ $isClickable }) => ($isClickable ? "none" : "auto")};

  &:hover {
    background: ${Color.lightGray};
  }
`;

const AttemptRowGrid = styled(Grid)`
  grid-template-columns: 3fr 2.5fr 120px 4fr;
  border-bottom: 1px solid ${Color.lightGrayHover};
  padding: 8px 16px 8px 48px;
  align-items: center;
  background: ${Color.lightGray};
`;

const SequenceBadge = styled(Flex)`
  background: ${Color.lightGrayHover};
  color: ${Color.textMain};
  width: 20px;
  height: 20px;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: bold;
`;

const LocationText = styled(Text)<{ $italic: boolean }>`
  font-style: ${({ $italic }) => ($italic ? "italic" : "normal")};
`;

interface ProductRowProps {
  detail: UpdateReportDetail;
  changeType: ChangeType;
  expandedProducts: Record<string, boolean>;
  toggleProduct: (key: string) => void;
  prodKey: string;
}

const ProductRow = ({
  detail,
  changeType,
  expandedProducts,
  toggleProduct,
  prodKey,
}: ProductRowProps) => {
  const { t, tDynamic } = useTranslation("common");
  const isProdCollapsed = !expandedProducts[prodKey];
  const isSkipped = detail.status === "SKIPPED";
  const failedAttempts =
    detail.placementAttempts?.filter((a: PlacementAttempt) => a.status === "FAIL") || [];
  const hasFailedAttempts = changeType === "INSERT" && failedAttempts.length > 0;

  const isOverallSuccess =
    changeType === "INSERT"
      ? detail.status === "SUCCESS" || detail.status === "PARTIALLY_APPLIED"
      : detail.status === "SUCCESS";

  const overallLocation = hasFailedAttempts
    ? "Multiple locations targeted"
    : detail.placementAttempts?.[0]?.locationId || "N/A";

  const { skipReasonCode } = detail;
  const overallError = isSkipped
    ? skipReasonCode && Object.hasOwn(flowUpdateSkipReasons, skipReasonCode)
      ? tDynamic(`updateReport.skipReasons.${skipReasonCode}`)
      : detail.skipReason || t("updateReport.skipReasons.UNKNOWN")
    : !isOverallSuccess
      ? hasFailedAttempts
        ? t("multiplePlacementFailures")
        : (detail.errorMessage ?? detail.status)
      : "";

  let iconName: "box" | "assortment" | "delete" | "arrowUp" | "arrowDown";

  if (changeType === "INSERT") {
    iconName = "box";
  } else if (changeType === "SWAP") {
    iconName = "assortment";
  } else if (changeType === "FACINGS_INCREASED") {
    iconName = "arrowUp";
  } else if (changeType === "FACINGS_DECREASED") {
    iconName = "arrowDown";
  } else {
    iconName = "delete";
  }

  return (
    <Flex column>
      <ProductRowGrid
        onClick={hasFailedAttempts ? () => toggleProduct(prodKey) : undefined}
        $isClickable={hasFailedAttempts}
      >
        {/* Item ID / Expander */}
        <Flex align="center" gap="10px">
          {hasFailedAttempts && (
            <Flex padding="4px">
              <ChevronWrapper $isOpen={!isProdCollapsed}>
                <Icon name="chevronRight" size={12} color={Color.textSecondary} />
              </ChevronWrapper>
            </Flex>
          )}
          {!hasFailedAttempts && <Flex width="20px" />}
          <Icon name={iconName} size={16} color={Color.textSecondary} />
          <Text bold>Product: {detail.productCode}</Text>
        </Flex>

        {/* Location */}
        <Flex>
          <LocationText
            $italic={hasFailedAttempts}
            color={hasFailedAttempts ? Color.textSecondary : Color.textMain}
          >
            {overallLocation}
          </LocationText>
        </Flex>

        {/* Status */}
        <Flex justify="center">
          {isSkipped ? (
            <Text variant="small2" color={Color.textSecondary}>
              {t("updateReport.skipped")}
            </Text>
          ) : (
            <Icon
              name={isOverallSuccess ? "checkmarkCircle" : "alert"}
              color={isOverallSuccess ? Color.primary : Color.red}
            />
          )}
        </Flex>

        {/* Error message */}
        <Flex>
          <Text color={!isOverallSuccess && !isSkipped ? Color.red : Color.textSecondary}>
            {overallError}
          </Text>
        </Flex>
      </ProductRowGrid>

      {/* Swap in / swap out sub-rows */}
      {changeType === "SWAP" && (
        <Flex column>
          <AttemptRowGrid>
            <Flex align="center" gap="10px">
              <Icon name="delete" size={14} color={Color.textSecondary} />
              <Text variant="small2" color={Color.textSecondary}>
                {t("itemSwappedOut")} {detail.swapOutProductCode ?? detail.productCode}
              </Text>
            </Flex>
            <Flex />
            <Flex />
            <Flex />
          </AttemptRowGrid>
          <AttemptRowGrid>
            <Flex align="center" gap="10px">
              <Icon name="box" size={14} color={Color.textSecondary} />
              <Text variant="small2" color={Color.textSecondary}>
                {t("itemSwappedIn")} {detail.swapInProductCode ?? t("na")}
              </Text>
            </Flex>
            <Flex />
            <Flex />
            <Flex />
          </AttemptRowGrid>
        </Flex>
      )}

      {/* Facings adjustment sub-row */}
      {(changeType === "FACINGS_INCREASED" || changeType === "FACINGS_DECREASED") && (
        <AttemptRowGrid>
          <Flex align="center" gap="10px">
            <Icon name={iconName} size={14} color={Color.textSecondary} />
            <Text variant="small2" color={Color.textSecondary}>
              {isSkipped ? (
                t("updateReport.skippedFacings", {
                  current: detail.oldFacings ?? t("na"),
                  target: detail.newFacings ?? t("na"),
                })
              ) : (
                <>
                  {t("facingsChange")} {detail.oldFacings ?? t("na")} →{" "}
                  {detail.newFacings ?? t("na")}
                </>
              )}
            </Text>
          </Flex>
          <Flex />
          <Flex />
          <Flex />
        </AttemptRowGrid>
      )}

      {/* Collapsible placement attempts */}
      {!isProdCollapsed && hasFailedAttempts && (
        <Flex column>
          {detail.placementAttempts?.map((attempt: PlacementAttempt, attemptIdx: number) => {
            const isAttemptSuccess = attempt.status === "SUCCESS";

            return (
              <AttemptRowGrid key={`${detail.productCode}-${attemptIdx}`}>
                <Flex align="center" gap="10px">
                  <SequenceBadge>{String(attemptIdx + 1).padStart(2, "0")}</SequenceBadge>
                  <Text variant="small2" color={Color.textSecondary}>
                    Attempt Sequence
                  </Text>
                </Flex>

                <Flex>
                  <Text variant="small2" color={Color.textSecondary}>
                    {attempt.locationId}
                  </Text>
                </Flex>

                <Flex justify="center">
                  <Icon
                    name={isAttemptSuccess ? "checkmarkCircle" : "clearCircle"}
                    color={isAttemptSuccess ? Color.primary : Color.red}
                    size={14}
                  />
                </Flex>

                <Flex>
                  <Text
                    variant="small2"
                    color={!isAttemptSuccess ? Color.red : Color.textSecondary}
                  >
                    {attempt.errorMessage || (!isAttemptSuccess ? "Placement error" : "")}
                  </Text>
                </Flex>
              </AttemptRowGrid>
            );
          })}
        </Flex>
      )}
    </Flex>
  );
};

const groupDetails = (details: UpdateReportDetail[]): GroupedStore[] => {
  const storeMap: Record<
    string,
    {
      insertions: UpdateReportDetail[];
      swaps: UpdateReportDetail[];
      deletions: UpdateReportDetail[];
      expansions: UpdateReportDetail[];
      reductions: UpdateReportDetail[];
    }
  > = {};

  for (const detail of details) {
    if (!storeMap[detail.storeCode]) {
      storeMap[detail.storeCode] = {
        insertions: [],
        swaps: [],
        deletions: [],
        expansions: [],
        reductions: [],
      };
    }

    if (detail.changeType === "INSERT") {
      storeMap[detail.storeCode].insertions.push(detail);
    } else if (detail.changeType === "SWAP") {
      storeMap[detail.storeCode].swaps.push(detail);
    } else if (detail.changeType === "DELETE") {
      storeMap[detail.storeCode].deletions.push(detail);
    } else if (detail.changeType === "FACINGS_INCREASED") {
      storeMap[detail.storeCode].expansions.push(detail);
    } else if (detail.changeType === "FACINGS_DECREASED") {
      storeMap[detail.storeCode].reductions.push(detail);
    }
  }

  return Object.keys(storeMap).map((storeCode) => {
    const { insertions, swaps, deletions, expansions, reductions } = storeMap[storeCode];

    let issueCount = 0;

    for (const item of insertions) {
      if (item.status === "SKIPPED") continue;
      const failedAttempts =
        item.placementAttempts?.filter((a: PlacementAttempt) => a.status === "FAIL") || [];
      if (item.placementAttempts && item.placementAttempts.length > 0) {
        if (failedAttempts.length > 0) {
          issueCount += 1;
        }
      } else if (item.status !== "SUCCESS") {
        issueCount += 1;
      }
    }

    for (const item of [...swaps, ...deletions, ...expansions, ...reductions]) {
      if (item.status !== "SUCCESS" && item.status !== "SKIPPED") {
        issueCount += 1;
      }
    }

    return {
      storeCode,
      issueCount,
      insertions,
      swaps,
      deletions,
      expansions,
      reductions,
    };
  });
};

interface PogIdLinkProps {
  label: string;
  storePogId: number | null;
  url: string | null;
}

const PogIdLink = ({ label, storePogId, url }: PogIdLinkProps) => (
  <PogLabel>
    {label}
    {storePogId !== null && url !== null ? (
      <PogLink href={url} onClick={(e) => e.stopPropagation()}>
        {storePogId}
      </PogLink>
    ) : (
      "—"
    )}
  </PogLabel>
);

export const UpdateReportGroupedTable = ({ details, storePogIds, buildStorePogUrl }: Props) => {
  const { t } = useTranslation("common");
  const groupedData = groupDetails(details);
  const getItemCountLabel = (count: number) =>
    count === 1 ? t("updateReport.item") : t("updateReport.items");

  // Collapse/Expand state maps
  const [collapsedStores, setCollapsedStores] = useState<Record<string, boolean>>({});
  const [collapsedOperations, setCollapsedOperations] = useState<Record<string, boolean>>({});
  const [expandedProducts, setExpandedProducts] = useState<Record<string, boolean>>({});

  const toggleStore = (storeCode: string) => {
    setCollapsedStores((prev) => ({ ...prev, [storeCode]: !prev[storeCode] }));
  };

  const toggleOperation = (key: string) => {
    setCollapsedOperations((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleProduct = (key: string) => {
    setExpandedProducts((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <TableContainer column>
      {/* Grid Headers */}
      <GridHeader>
        <HeaderCell>Entity / Item Identifier</HeaderCell>
        <HeaderCell>Location / Shelf ID</HeaderCell>
        <HeaderCell textAlign="center">Status</HeaderCell>
        <HeaderCell>Error Detail / Message</HeaderCell>
      </GridHeader>

      {/* Main Accordion Rows */}
      {groupedData.map((store) => {
        const isStoreCollapsed = !!collapsedStores[store.storeCode];

        const pogIds = storePogIds[store.storeCode] ?? {
          previousStorePogId: null,
          updatedStorePogId: null,
        };

        return (
          <Flex key={store.storeCode} column>
            {/* Store Row */}
            <StoreHeaderRow
              onClick={() => toggleStore(store.storeCode)}
              aria-expanded={!isStoreCollapsed}
            >
              <Icon name="stores" color={Color.primaryActive} />
              <Text variant="h5" color={Color.primaryActive}>
                Store #{store.storeCode}
              </Text>
              {store.issueCount > 0 && (
                <IssueBadge>
                  {store.issueCount} {store.issueCount === 1 ? "ISSUE" : "ISSUES"}
                </IssueBadge>
              )}
              <PogIdLink
                label="Prev POG: "
                storePogId={pogIds.previousStorePogId}
                url={
                  pogIds.previousStorePogId !== null
                    ? buildStorePogUrl(store.storeCode, pogIds.previousStorePogId)
                    : null
                }
              />
              <PogIdLink
                label="New POG: "
                storePogId={pogIds.updatedStorePogId}
                url={
                  pogIds.updatedStorePogId !== null
                    ? buildStorePogUrl(store.storeCode, pogIds.updatedStorePogId)
                    : null
                }
              />
              <Flex flexGrow justify="right">
                <ChevronWrapper $isOpen={!isStoreCollapsed}>
                  <Icon name="chevronRight" size={14} color={Color.textSecondary} />
                </ChevronWrapper>
              </Flex>
            </StoreHeaderRow>

            {/* Store Operations */}
            {!isStoreCollapsed && (
              <Flex column>
                {/* 1. Insertions */}
                {store.insertions.length > 0 && (
                  <Flex column>
                    {(() => {
                      const opKey = `${store.storeCode}-INSERT`;
                      const isOpCollapsed = !!collapsedOperations[opKey];
                      const totalItems = store.insertions.length;

                      return (
                        <>
                          <OperationHeaderRow
                            onClick={() => toggleOperation(opKey)}
                            aria-expanded={!isOpCollapsed}
                          >
                            <ChevronWrapper $isOpen={!isOpCollapsed}>
                              <Icon name="chevronRight" size={12} color={Color.textSecondary} />
                            </ChevronWrapper>
                            <Text variant="small1" color={Color.textSecondary} bold>
                              INSERTIONS ({totalItems} {getItemCountLabel(totalItems)})
                            </Text>
                          </OperationHeaderRow>

                          {!isOpCollapsed &&
                            store.insertions.map((detail, idx) => {
                              const prodKey = `${store.storeCode}-INSERT-${detail.productCode}-${idx}`;
                              return (
                                <ProductRow
                                  key={prodKey}
                                  detail={detail}
                                  changeType="INSERT"
                                  expandedProducts={expandedProducts}
                                  toggleProduct={toggleProduct}
                                  prodKey={prodKey}
                                />
                              );
                            })}
                        </>
                      );
                    })()}
                  </Flex>
                )}

                {/* 2. Swaps */}
                {store.swaps.length > 0 && (
                  <Flex column>
                    {(() => {
                      const opKey = `${store.storeCode}-SWAP`;
                      const isOpCollapsed = !!collapsedOperations[opKey];
                      const totalItems = store.swaps.length;

                      return (
                        <>
                          <OperationHeaderRow
                            onClick={() => toggleOperation(opKey)}
                            aria-expanded={!isOpCollapsed}
                          >
                            <ChevronWrapper $isOpen={!isOpCollapsed}>
                              <Icon name="chevronRight" size={12} color={Color.textSecondary} />
                            </ChevronWrapper>
                            <Text variant="small1" color={Color.textSecondary} bold>
                              SWAPS ({totalItems} {getItemCountLabel(totalItems)})
                            </Text>
                          </OperationHeaderRow>

                          {!isOpCollapsed &&
                            store.swaps.map((detail, idx) => {
                              const prodKey = `${store.storeCode}-SWAP-${detail.productCode}-${idx}`;
                              return (
                                <ProductRow
                                  key={prodKey}
                                  detail={detail}
                                  changeType="SWAP"
                                  expandedProducts={expandedProducts}
                                  toggleProduct={toggleProduct}
                                  prodKey={prodKey}
                                />
                              );
                            })}
                        </>
                      );
                    })()}
                  </Flex>
                )}

                {/* 3. Deletions */}
                {store.deletions.length > 0 && (
                  <Flex column>
                    {(() => {
                      const opKey = `${store.storeCode}-DELETE`;
                      const isOpCollapsed = !!collapsedOperations[opKey];
                      const totalItems = store.deletions.length;

                      return (
                        <>
                          <OperationHeaderRow
                            onClick={() => toggleOperation(opKey)}
                            aria-expanded={!isOpCollapsed}
                          >
                            <ChevronWrapper $isOpen={!isOpCollapsed}>
                              <Icon name="chevronRight" size={12} color={Color.textSecondary} />
                            </ChevronWrapper>
                            <Text variant="small1" color={Color.textSecondary} bold>
                              DELETIONS ({totalItems} {getItemCountLabel(totalItems)})
                            </Text>
                          </OperationHeaderRow>

                          {!isOpCollapsed &&
                            store.deletions.map((detail, idx) => {
                              const prodKey = `${store.storeCode}-DELETE-${detail.productCode}-${idx}`;
                              return (
                                <ProductRow
                                  key={prodKey}
                                  detail={detail}
                                  changeType="DELETE"
                                  expandedProducts={expandedProducts}
                                  toggleProduct={toggleProduct}
                                  prodKey={prodKey}
                                />
                              );
                            })}
                        </>
                      );
                    })()}
                  </Flex>
                )}

                {/* 4. Facings Expansions */}
                {store.expansions.length > 0 && (
                  <Flex column>
                    {(() => {
                      const opKey = `${store.storeCode}-FACINGS_INCREASED`;
                      const isOpCollapsed = !!collapsedOperations[opKey];
                      const totalItems = store.expansions.length;

                      return (
                        <>
                          <OperationHeaderRow
                            onClick={() => toggleOperation(opKey)}
                            aria-expanded={!isOpCollapsed}
                          >
                            <ChevronWrapper $isOpen={!isOpCollapsed}>
                              <Icon name="chevronRight" size={12} color={Color.textSecondary} />
                            </ChevronWrapper>
                            <Text variant="small1" color={Color.textSecondary} bold>
                              {t("updateReport.facingsExpanded")} ({totalItems}{" "}
                              {getItemCountLabel(totalItems)})
                            </Text>
                          </OperationHeaderRow>

                          {!isOpCollapsed &&
                            store.expansions.map((detail, idx) => {
                              const prodKey = `${store.storeCode}-FACINGS_INCREASED-${detail.productCode}-${idx}`;
                              return (
                                <ProductRow
                                  key={prodKey}
                                  detail={detail}
                                  changeType="FACINGS_INCREASED"
                                  expandedProducts={expandedProducts}
                                  toggleProduct={toggleProduct}
                                  prodKey={prodKey}
                                />
                              );
                            })}
                        </>
                      );
                    })()}
                  </Flex>
                )}

                {/* 5. Facings Reductions */}
                {store.reductions.length > 0 && (
                  <Flex column>
                    {(() => {
                      const opKey = `${store.storeCode}-FACINGS_DECREASED`;
                      const isOpCollapsed = !!collapsedOperations[opKey];
                      const totalItems = store.reductions.length;

                      return (
                        <>
                          <OperationHeaderRow
                            onClick={() => toggleOperation(opKey)}
                            aria-expanded={!isOpCollapsed}
                          >
                            <ChevronWrapper $isOpen={!isOpCollapsed}>
                              <Icon name="chevronRight" size={12} color={Color.textSecondary} />
                            </ChevronWrapper>
                            <Text variant="small1" color={Color.textSecondary} bold>
                              {t("updateReport.facingsReduced")} ({totalItems}{" "}
                              {getItemCountLabel(totalItems)})
                            </Text>
                          </OperationHeaderRow>

                          {!isOpCollapsed &&
                            store.reductions.map((detail, idx) => {
                              const prodKey = `${store.storeCode}-FACINGS_DECREASED-${detail.productCode}-${idx}`;
                              return (
                                <ProductRow
                                  key={prodKey}
                                  detail={detail}
                                  changeType="FACINGS_DECREASED"
                                  expandedProducts={expandedProducts}
                                  toggleProduct={toggleProduct}
                                  prodKey={prodKey}
                                />
                              );
                            })}
                        </>
                      );
                    })()}
                  </Flex>
                )}
              </Flex>
            )}
          </Flex>
        );
      })}
    </TableContainer>
  );
};
